import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { API_BASE_URL } from "@/lib/api";
import { toast } from "@/hooks/use-toast";
import { ManualVerification } from "@/components/ManualVerification";

type VerifyState = "verifying" | "manual";

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<VerifyState>("manual");
  const [helperText, setHelperText] = useState<string | undefined>(undefined);

  const initialEmail = useMemo(() => {
    return (
      searchParams.get("email") ||
      sessionStorage.getItem("verificationEmail") ||
      ""
    );
  }, [searchParams]);

  useEffect(() => {
    if (initialEmail) {
      sessionStorage.setItem("verificationEmail", initialEmail);
    }
  }, [initialEmail]);

  useEffect(() => {
    const token = searchParams.get("token");

    if (!token) {
      setState("manual");
      return;
    }

    setState("verifying");

    fetch(`${API_BASE_URL}/api/auth/verify-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    })
      .then(async (res) => {
        const data = await res.json();

        if (res.ok) {
          sessionStorage.removeItem("verificationEmail");
          toast({
            title: "Email verified!",
            description: "You can now sign in.",
          });
          navigate("/auth?tab=login");
          return;
        }

        setHelperText(
          data.message ||
            "That verification link is no longer valid. Enter the 6-digit code from your email or send yourself a fresh one.",
        );
        setState("manual");
      })
      .catch(() => {
        setHelperText(
          "I couldn't verify that link automatically. Enter the 6-digit code from your email or send a new one.",
        );
        setState("manual");
      });
  }, [navigate, searchParams]);

  if (state === "verifying") {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="max-w-md w-full rounded-lg border bg-card p-6 text-center shadow-sm">
          <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-primary" />
          <h1 className="text-xl font-semibold mb-2">Verifying your email</h1>
          <p className="text-sm text-muted-foreground">
            Hang tight, I’m confirming your account now.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <div className="max-w-md w-full">
        <ManualVerification
          initialEmail={initialEmail}
          helperText={helperText}
        />
      </div>
    </div>
  );
}
